import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isANumber } from 'utils/number';
import * as surveyApi from 'api/SurveyApi';
import { RootState, TThunkApi } from 'store/store';
import * as manApi from 'api/ManApi';
import { getOrCreateTag } from 'store/providers/providerSlice';
import cloneDeep from 'lodash/cloneDeep';
import { requireSeveralUser } from 'store/users/usersSlice';
import { getEffectiveName } from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/UniqueQuestion/PeriodTypeSelector/DatePeriod';
import {
  ICheckPoint,
  IFileAnswerView,
  IMultipleAnswerQuestionView,
  IMultipleChoiceAnswerView,
  IQuestionsView,
  IReportView,
  ISurveyItem,
  ISurveyProperties,
  ISurveyPropertiesDB,
  ISurveyResponseView,
  ISurveys,
  ISurveyVersionInfo,
  ISurveyView,
  ISurveyViewServer,
  PeriodType,
  QuestionState,
  TimeScale,
  UploadProgressState,
} from './surveyTypes';
import { ITagView } from '../providers/providerTypes';
import { survey_addResponse as providerAddSurveyResponse } from '../providers/providerSlice';
import { IQueryFilter } from '../entity/IQueryFilter';
import { IAnalyticsQuery } from '../entity/IAnalyticsQuery';
import { IExportResponsesQuery } from '../entity/IExportResponsesQuery';
import { IAnalyticsListQuery } from '../entity/IAnalyticsListQuery';
import { NumberOrNull } from '../entity/types';
import { updateNestedObject } from '../objectHelpers';
import { getCalendarLocal } from '../../i18n';

export const initialUploadProgress: UploadProgressState = { byQuestion: {} };

const initialState: ISurveys = {
  data: {},
  reports: { data: {}, loaded: false },
  // @ts-expect-error it's ok
  checkPoints: { data: {}, loaded: false },
  cumulativePercentage: {},
  uploadProgress: initialUploadProgress,
};
// Helpers
function manipulateSurvey(dbSurvey: ISurveyViewServer, surveyToken: string) {
  // @ts-expect-error it's ok. updating step by step
  const ans: ISurveyView = { ...dbSurvey };
  ans.surveyToken = surveyToken;
  ans.versions = Object.values(dbSurvey.versions).reduce(
    (
      obj: { [versionId: string]: ISurveyProperties },
      dbItem: ISurveyPropertiesDB
    ) => {
      const item: ISurveyProperties = {
        ...dbItem,
        questionMap: (dbItem.questionList || []).reduce((list, item) => {
          if (item.id) {
            list[item.id] = item;
          }
          return list;
        }, {}),
      };
      updateQuestionOrder(Object.values(item.questionMap || {}));
      obj[item.versionInfo!.versionId!] = item;
      return obj;
    },
    {} as { [versionId: string]: ISurveyProperties }
  );
  ans.notifications = dbSurvey.notifications || [];
  ans.notifGroups = dbSurvey.notifGroups || [];
  ans.allowEditNotifGroupMembersTo =
    dbSurvey.allowEditNotifGroupMembersTo || [];

  if (dbSurvey.surveyQuestionList) {
    ans.questionMap = dbSurvey.surveyQuestionList.reduce(
      (map: any, obj: any) => {
        map[obj.id] = { data: { ...obj }, state: 'exists' };
        return map;
      },
      {}
    );
    updateQuestionOrder(
      Object.values(ans.questionMap || {}).map((e) => e.data)
    );
  } else {
    ans.questionMap = {};
  }
  return ans;
}

function normalizeResponse(
  responseJS: ISurveyResponseView
): ISurveyResponseView {
  if (responseJS.surveyAnswerList)
    responseJS.surveyAnswerList = responseJS.surveyAnswerList.map((value) => {
      if (value.type !== 'multipleChoice') {
        return value;
      }
      const ret = value as IMultipleChoiceAnswerView;
      if (ret.value && !ret.values.includes(ret.value))
        ret.values.push(ret.value);
      return ret;
    });
  return responseJS as ISurveyResponseView;
}

function checkAndFixData(
  state: ISurveys,
  firstId: string | number,
  isResponse: boolean,
  lastId: string | number
) {
  if (!state.data[firstId]) {
    state.data = {
      ...state.data,
      [firstId]: {},
    };
  }
  if (!state.data[firstId]?.data) {
    state.data[firstId] = {
      ...state.data[firstId],
      data: {},
    };
  }
  if (isResponse && !state.data[firstId].data?.responseMap) {
    state.data[firstId].data = {
      ...state.data[firstId].data,
      responseMap: {},
    };
  }
  if (!isResponse && !state.data[firstId].data?.questionMap) {
    state.data[firstId].data = {
      ...state.data[firstId].data,
      questionMap: {},
    };
  }

  if (isResponse && !state.data[firstId].data.responseMap[lastId]) {
    state.data[firstId].data.responseMap = {
      ...state.data[firstId].data.responseMap,
      [lastId]: {},
    };
  }
  if (!isResponse && !state.data[firstId].data.questionMap[lastId]) {
    state.data[firstId].data.questionMap[lastId] = {
      ...state.data[firstId].data.questionMap,
      [lastId]: {},
    };
  }
}

const getUsersIdsFromResponses = ({
  responses,
  includeInstanceAsUserId,
}: {
  responses: ISurveyResponseView[];
  includeInstanceAsUserId: boolean;
}) => {
  const usersIds = responses.reduce(
    (list: Array<number>, response: ISurveyResponseView) => {
      list.push(response.userId as number);
      if (includeInstanceAsUserId) list.push(response.instance as number);
      if (response.changeLog) {
        response.changeLog.forEach((change) => {
          change.userId && list.push(change.userId);
        });
      }
      return list;
    },
    []
  );

  return Array.from(new Set(usersIds));
};

const processResponses = async ({
  responses,
  surveyItemData,
  thisUserId,
  dispatch,
}: {
  responses: ISurveyResponseView[];
  surveyItemData: ISurveyItem;
  thisUserId: number | undefined;
  dispatch: TThunkApi['dispatch'];
}) => {
  let usersSet: Array<number> = [];
  if (responses.length > 0) {
    usersSet = getUsersIdsFromResponses({
      responses,
      includeInstanceAsUserId: surveyItemData.instanceType === 'User',
    });
  } else if (thisUserId) {
    usersSet.push(thisUserId);
  }

  return dispatch(requireSeveralUser({ usersList: usersSet }))
    .then(() => {
      dispatch(
        addSurveyResponses({
          surveyId: surveyItemData.surveyId,
          responses: responses,
        })
      );

      if (responses.length < 20) {
        dispatch(allResponsesLoaded({ surveyId: surveyItemData.surveyId }));
      }
      return Promise.resolve({ responses });
    })
    .catch((err: any) => {
      return Promise.reject(err);
    });
};

const uploadSurveyFiles = async (
  fileQuestions: IFileAnswerView[],
  responseAnswer: ISurveyResponseView['surveyAnswerList'],
  ThunkApi: TThunkApi
) => {
  const qids = fileQuestions.map((q: any) =>
    String((q as any).questionId ?? q.id)
  );
  ThunkApi.dispatch(initUploadProgressAction(qids));
  const files = fileQuestions.reduce(
    (
      temp: { [questionId: string]: { url: string; blob: string } },
      answer: IFileAnswerView
    ) => {
      const { file, questionId } = answer;
      const {
        file: { url: blob },
      } = responseAnswer.find(
        (ans) => ans.questionId === questionId
      ) as IFileAnswerView;
      temp[questionId] = {
        url: file.url,
        blob,
      };
      return temp;
    },
    {}
  );
  try {
    await surveyApi.uploadSurveyFiles(files, (questionId, percent) => {
      ThunkApi.dispatch(setQuestionProgressAction({ questionId, percent }));
    });
    return Promise.resolve();
  } catch (err) {
    return Promise.reject(err);
  } finally {
    ThunkApi.dispatch(clearUploadProgressAction());
  }
};

// Reducers
function loadingSurveyHandler(
  state: ISurveys,
  action: PayloadAction<{ surveyId: number; promise: any }>
) {
  if (!state.data[action.payload.surveyId]?.isLoading) {
    state.data = {
      ...state.data,
      [action.payload.surveyId]: state.data[action.payload.surveyId] || {
        isLoading: true,
      },
    };
  }
}

function addSurveyHandler(
  state: ISurveys,
  action: PayloadAction<{
    survey: ISurveyViewServer;
    providerItemId: number;
    providerId: number;
    surveyToken: string;
  }>
) {
  const survey = manipulateSurvey(
    action.payload.survey,
    action.payload.surveyToken
  );
  const oldResponseMap =
    state.data[action.payload.survey.id!]?.data?.responseMap;
  survey.responseMap = oldResponseMap || {};
  state.data[action.payload.survey.id!] = {
    data: survey,
    isLoading: false,
    isValid: true,
    allResponsesLoaded: false,
    viewingVersion: survey.currentVersion,
    reports: { data: {}, loaded: false },
  };
}

function surveySetViewVersionHandler(
  state: ISurveys,
  action: PayloadAction<{ surveyId: number; versionId: string }>
) {
  if (!state.data[action.payload.surveyId]) {
    // @ts-expect-error updating step by step
    state.data = {
      ...state.data,
      [action.payload.surveyId]: {},
    };
  }

  if (!state.data[action.payload.surveyId].viewingVersion) {
    state.data[action.payload.surveyId] = {
      ...state.data[action.payload.surveyId],
      viewingVersion: '',
    };
  }

  state.data[action.payload.surveyId].viewingVersion = action.payload.versionId;
}

function failToAddSurveyHandler(
  state: ISurveys,
  action: PayloadAction<{ surveyId: number; err: any }>
) {
  state.data[action.payload.surveyId] = {
    isLoading: false,
    isValid: false,
    // @ts-expect-error it's ok
    data: {},
    err: action.payload.err,
  };
}

function addQuestionHandler(
  state: ISurveys,
  action: PayloadAction<{ surveyId: number; question: IQuestionsView }>
) {
  checkAndFixData(
    state,
    action.payload.surveyId,
    false,
    action.payload.question.id
  );

  state.data[action.payload.surveyId].data.questionMap![
    action.payload.question.id
  ] = {
    data: action.payload.question,
    state: 'creating',
  };
  updateQuestionOrder(
    Object.values(
      state.data[action.payload.surveyId].data.questionMap || {}
    ).map((e) => e.data)
  );
}

function updateQuestionOrder(questions: IQuestionsView[]) {
  let indexCounter = 1;
  questions
    .sort((a: IQuestionsView, b: IQuestionsView) => a.order - b.order)
    .forEach((question: IQuestionsView) => {
      const id = question.id;
      if (id) {
        if (!question.deleted) {
          question.indexOrder = indexCounter++;
        }
      }
    });
}

function updateQuestionHandler(
  state: ISurveys,
  action: PayloadAction<{
    surveyId: number;
    oldQuestion: IQuestionsView | IMultipleAnswerQuestionView;
    newQuestion: IQuestionsView | IMultipleAnswerQuestionView;
    enableEditing?: boolean;
  }>
) {
  const currentVersionId =
    state.data[action.payload.surveyId]?.data?.currentVersion;
  if (!currentVersionId) return;
  const versions = state.data[action.payload.surveyId]?.data?.versions;
  if (!versions) return;
  const currentVersion = versions?.[currentVersionId];
  if (!currentVersion) return;
  const questionObj = {
    data: action.payload.newQuestion,
    state: action.payload?.enableEditing
      ? 'editing'
      : ('exists' as QuestionState),
  };

  checkAndFixData(
    state,
    action.payload.surveyId,
    false,
    action.payload.newQuestion.id
  );

  state.data[action.payload.surveyId].data.questionMap![
    action.payload.newQuestion.id
  ] = questionObj;

  currentVersion.questionMap![action.payload.newQuestion.id] =
    action.payload.newQuestion;

  // const targetQuestionListItemId = currentVersion.questionList?.find((item) => {
  //   return item.id === action.payload.newQuestion.id;
  // }).id;
  // const targetQuestionListItemId = currentVersion.questionList!.findIndex(
  //   (item) => item.id === action.payload.newQuestion.id
  // );
  // currentVersion.questionList![targetQuestionListItemId] =
  //   action.payload.newQuestion;
  updateQuestionOrder(Object.values(currentVersion.questionMap || {}));
  currentVersion.questionList = Object.values(currentVersion.questionMap || {});
  // updateQuestionOrder(
  //   Object.values(
  //     state.data[action.payload.surveyId].data.questionMap || {}
  //   ).map((e) => e.data)
  // );
  if (action.payload.newQuestion.id !== action.payload.oldQuestion.id) {
    delete state.data[action.payload.surveyId].data!.questionMap![
      action.payload.oldQuestion.id
    ];
  }
}

function failToUpdateQuestionHandler(
  state: ISurveys,
  action: PayloadAction<{
    question: IQuestionsView | IMultipleAnswerQuestionView;
    err: any;
    newState: QuestionState;
    surveyId: number;
  }>
) {
  checkAndFixData(
    state,
    action.payload.surveyId,
    false,
    action.payload.question.id
  );

  state.data[action.payload.surveyId].data.questionMap![
    action.payload.question.id
  ] = {
    data: action.payload.question,
    state: action.payload.newState,
    err: action.payload.err,
  };
}

function changeQuestionStateHandler(
  state: ISurveys,
  action: PayloadAction<{
    questionId: number;
    surveyId: number;
    state: QuestionState;
  }>
) {
  if (
    !state.data[action.payload.surveyId]?.data?.questionMap?.[
      action.payload.questionId
    ].state
  )
    return;

  if (action.payload.state === 'removed') {
    delete state.data[action.payload.surveyId]?.data?.questionMap?.[
      action.payload.questionId
    ];
  } else {
    state.data[action.payload.surveyId].data.questionMap![
      action.payload.questionId
    ].state = action.payload.state;
  }
}

function addResponseHandler(
  state: ISurveys,
  action: PayloadAction<{
    response: ISurveyResponseView;
    surveyId: number;
    providerId: number;
    itemId: number;
    effectiveDateString: string;
  }>
) {
  if (!state.data[action.payload.surveyId]) {
    // @ts-expect-error it's updating step by step
    state.data = {
      ...state.data,
      [action.payload.surveyId]: {
        data: {
          responseMap: {},
        },
      },
    };
  }
  if (!state.data[action.payload.surveyId!].data.responseMap)
    state.data[action.payload.surveyId!].data.responseMap = {};
  if (
    !state.data[action.payload.surveyId!].data.responseMap![
      action.payload.response.id!
    ]
  ) {
    state.data[action.payload.surveyId]!.data!.responseMap = {
      ...state.data[action.payload.surveyId]?.data?.responseMap,
      [action.payload.response.id!]: {},
    };
  }

  state.data[action.payload.surveyId].data.responseMap![
    action.payload.response.id!
  ] = normalizeResponse(action.payload.response);
}

function initUploadProgressHandler(
  state: ISurveys,
  action: PayloadAction<string[]>
) {
  state.uploadProgress.byQuestion = {};
  for (const qid of action.payload) state.uploadProgress.byQuestion[qid] = 0;
}

function setQuestionProgressHandler(
  state: ISurveys,
  action: PayloadAction<{ questionId: string; percent: number }>
) {
  const { questionId, percent } = action.payload;
  state.uploadProgress.byQuestion[questionId] = percent;
}

function clearUploadProgressHandler(state: ISurveys) {
  state.uploadProgress.byQuestion = {};
}

function addSurveyResponsesHandler(
  state: ISurveys,
  action: PayloadAction<{
    surveyId: number;
    responses: Array<ISurveyResponseView>;
  }>
) {
  action.payload.responses.forEach((res) => {
    if (!res.id) return;
    const responseJS = normalizeResponse(res) as ISurveyResponseView;
    checkAndFixData(state, action.payload.surveyId, true, res.id);
    state.data[action.payload.surveyId].data.responseMap![res.id] = responseJS;
  });
}

function allResponsesLoadedHandler(
  state: ISurveys,
  action: PayloadAction<{ surveyId: number }>
) {
  state.data[action.payload.surveyId].allResponsesLoaded = true;
}

function removeSurveyResponseHandler(
  state: ISurveys,
  action: PayloadAction<{ responseId: number; surveyId: number }>
) {
  delete state.data[action.payload.surveyId].data.responseMap?.[
    action.payload.responseId
  ];
}

function addSurveyResponseHandler(
  state: ISurveys,
  action: PayloadAction<{
    response: ISurveyResponseView;
    surveyId: number;
    providerId: number;
    itemId: number;
    effectiveDateString: string;
  }>
) {
  if (!action.payload.response.id) return;
  checkAndFixData(
    state,
    action.payload.surveyId,
    true,
    action.payload.response.id
  );
  state.data[action.payload.surveyId].data.responseMap![
    action.payload.response.id
  ] = normalizeResponse(action.payload.response);
}

function clearAndAddSeveralReportsHandler(
  state: ISurveys,
  action: PayloadAction<{
    reports: Array<IReportView>;
    clearCurrent?: boolean;
    setLoadedForSurvey?: boolean;
  }>
) {
  const {
    reports,
    clearCurrent = false,
    setLoadedForSurvey = false,
  } = action.payload;

  state.reports.loaded = true;

  if (clearCurrent) {
    state.reports.data = {};
  }

  reports.forEach((report) => {
    state.reports.data[report.id] = report;

    if (!report.surveyId) return;

    if (report.surveyId) {
      state = updateNestedObject<ISurveys>({
        obj: state,
        path: `data[${report.surveyId}].reports.data[${report.id}]`,
        value: report,
      });
      if (setLoadedForSurvey) {
        state = updateNestedObject<ISurveys>({
          obj: state,
          path: `data[${report.surveyId}].reports.loaded`,
          value: true,
        });
      }
    }
  });
}

function addSurveyReportHandler(
  state: ISurveys,
  action: PayloadAction<{ report: IReportView }>
) {
  if (!action.payload.report.id) return;
  state.reports.data[action.payload.report.id] = action.payload.report;
}

function updateSurveyReportColorMapHandler(
  state: ISurveys,
  action: PayloadAction<{ reportId: number; colorMap: Record<string, string> }>
) {
  if (Object.values(action.payload.colorMap).length > 0) {
    state.reports.data[action.payload.reportId] = {
      ...state.reports.data[action.payload.reportId],
      colorMap: action.payload.colorMap,
    };
  }
}

function updateSurveyReportHandler(
  state: ISurveys,
  action: PayloadAction<{ report: IReportView }>
) {
  if (!action.payload.report.id) return;
  state.reports.data[action.payload.report.id] = action.payload.report;
}

function removeSurveyReportHandler(
  state: ISurveys,
  action: PayloadAction<{ reportId: number }>
) {
  delete state.reports.data[action.payload.reportId];
}

function setSeveralCheckPointsHandler(
  state: ISurveys,
  action: PayloadAction<{ checkPoints: Array<ICheckPoint> }>
) {
  state.checkPoints.loaded = true;
  action.payload.checkPoints.forEach((checkPoint) => {
    if (!checkPoint.id) return;
    state.checkPoints.data[checkPoint.id] = checkPoint;
  });
}

function setCheckPointHandler(
  state: ISurveys,
  action: PayloadAction<{ checkPoint: ICheckPoint }>
) {
  if (!action.payload.checkPoint.id) return;
  state.checkPoints.data[action.payload.checkPoint.id] =
    action.payload.checkPoint;
}

function deleteCheckPointHandler(
  state: ISurveys,
  action: PayloadAction<{ checkPointId: number }>
) {
  delete state.checkPoints.data[action.payload.checkPointId];
}

function setSeveralSurveyHandler(
  state: ISurveys,
  action: PayloadAction<{
    surveys: {
      [surveyToken: string]: ISurveyViewServer;
    };
  }>
) {
  Object.keys(action.payload.surveys).forEach((surveyToken) => {
    const survey = action.payload.surveys[surveyToken];
    state.data[survey.id] = {
      data: manipulateSurvey(survey, surveyToken),
      isLoading: false,
      isValid: true,
      allResponsesLoaded: false,
      reports: { data: {}, loaded: false },
    };
  });
}

function cumulativePercentageReadyHandler(
  state: ISurveys,
  action: PayloadAction<{
    groupId: string;
    key: string;
    percentage: number;
    boundPercentage: number;
  }>
) {
  let current = state.cumulativePercentage[action.payload.groupId];
  if (current === null || current === undefined) {
    current = {
      id: action.payload.groupId,
      avg: 0,
      boundAvg: 0,
      items: {},
    };
  }

  const currentItem = current.items[action.payload.key];
  const items = current.items;
  const count = Object.keys(items).length;
  let boundSum = current.boundAvg * count;
  let sum = current.avg * count;
  if (currentItem !== undefined) {
    return;
  }

  if (isANumber(action.payload.percentage)) {
    sum += action.payload.percentage;
  }

  if (isANumber(action.payload.boundPercentage)) {
    boundSum += action.payload.boundPercentage;
  }

  items[action.payload.key] = {
    key: action.payload.key,
    itemAvg: action.payload.percentage,
    itemBoundAvg: action.payload.boundPercentage,
  };

  current.items = items;
  current.avg = count === 0 ? 0 : sum / count;
  current.boundAvg = count === 0 ? 0 : boundSum / count;
  state.cumulativePercentage[action.payload.groupId] = current;
}

function cumulativePercentageResetHandler(
  state: ISurveys,
  action: PayloadAction<{ groupId: string }>
) {
  let current = state.cumulativePercentage[action.payload.groupId];
  if (current === null || current === undefined) {
    return;
  }
  current = {
    id: action.payload.groupId,
    avg: 0,
    boundAvg: 0,
    items: {},
  };
  state.cumulativePercentage[action.payload.groupId] = current;
}

// Thunks

export const loadSurveyFn = async (
  { providerSurveyItemData }: { providerSurveyItemData: ISurveyItem },
  ThunkApi: TThunkApi
) => {
  const surveyToken =
    providerSurveyItemData.editToken ||
    providerSurveyItemData.changeDataToken ||
    providerSurveyItemData.reportToken ||
    providerSurveyItemData.fillToken;

  const promise = surveyApi.getSurvey(surveyToken).then(
    (res) => {
      ThunkApi.dispatch(
        addSurvey({
          survey: res.data,
          providerItemId: providerSurveyItemData.id,
          providerId: providerSurveyItemData.providerId,
          surveyToken: surveyToken,
        })
      );

      return Promise.resolve({ survey: res.data });
    },
    (err) => {
      ThunkApi.dispatch(
        failToAddSurvey({
          surveyId: providerSurveyItemData.surveyId,
          err,
        })
      );

      return ThunkApi.rejectWithValue(err) as any;
    }
  );
  ThunkApi.dispatch(
    loadingSurvey({
      surveyId: providerSurveyItemData.surveyId,
      promise: promise,
    })
  );

  return promise;
};
const loadSurvey = createAsyncThunk('survey/loadSurvey', loadSurveyFn);

export const requireSurveyFn = async (
  {
    providerSurveyItemData,
    forceReload = false,
  }: {
    providerSurveyItemData: ISurveyItem;
    forceReload?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const surveyImmutable = store.survey.data?.[providerSurveyItemData?.surveyId];
  if (surveyImmutable === undefined || forceReload) {
    return ThunkApi.dispatch(
      loadSurvey({
        providerSurveyItemData: providerSurveyItemData,
      })
    );
  }
  return Promise.resolve();
};
const requireSurvey = createAsyncThunk('survey/requireSurvey', requireSurveyFn);

export const updateSurveyFn = async (
  {
    surveyData,
    providerSurveyItemData,
    questionList,
  }: {
    surveyData: ISurveyView;
    providerSurveyItemData: ISurveyItem;
    questionList?: Array<IQuestionsView>;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const copiedSurveyData = cloneDeep(surveyData);
  delete copiedSurveyData.questionMap;
  // copiedSurveyData.versions = undefined as any;
  const surveyToken =
    providerSurveyItemData.editToken ||
    providerSurveyItemData.changeDataToken ||
    providerSurveyItemData.reportToken ||
    providerSurveyItemData.fillToken;
  const customSurveyData = copiedSurveyData as any as ISurveyViewServer;
  if (questionList && questionList?.length > 0) {
    // customSurveyData.versions[customSurveyData.currentVersion].questionMap =
    //   questionList.reduce((acc: any, item) => {
    //     acc[item.id] = item;
    //     return acc;
    //   }, {});
    // (
    //   customSurveyData.versions[customSurveyData.currentVersion] as any
    // ).keepSubmitQuestionList = true;

    // customSurveyData.versions[customSurveyData.currentVersion].questionList =
    //   questionList;
    customSurveyData.surveyQuestionList = questionList;
    (customSurveyData as any).keepSubmitQuestionList = true;
  }
  if (customSurveyData.versions)
    customSurveyData.versions = Object.values(customSurveyData.versions);

  return surveyApi.surveyUpdate(customSurveyData, surveyToken, store).then(
    (res) => {
      ThunkApi.dispatch(
        addSurvey({
          survey: res.data,
          providerItemId: providerSurveyItemData.id,
          providerId: providerSurveyItemData.providerId,
          surveyToken: surveyToken,
        })
      );
      return Promise.resolve({ survey: res.data });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const updateSurvey = createAsyncThunk('survey/updateSurvey', updateSurveyFn);

export const createSurveyVersionFn = async (
  {
    versionInfo,
    providerSurveyItemData,
  }: {
    versionInfo: ISurveyVersionInfo;
    providerSurveyItemData: ISurveyItem;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const surveyToken =
    providerSurveyItemData.editToken ||
    providerSurveyItemData.changeDataToken ||
    providerSurveyItemData.reportToken ||
    providerSurveyItemData.fillToken;

  return surveyApi.createNewVersion(versionInfo, surveyToken, store).then(
    (res) => {
      ThunkApi.dispatch(
        addSurvey({
          survey: res.data,
          providerItemId: providerSurveyItemData.id,
          providerId: providerSurveyItemData.providerId,
          surveyToken: surveyToken,
        })
      );
      return Promise.resolve({ survey: res.data });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const createSurveyVersion = createAsyncThunk(
  'survey/createSurveyVersion',
  createSurveyVersionFn
);

// export const fillingOutSurveyViaAIFn = async ({}: {}, ThunkApi: TThunkApi) => {
//   const store = ThunkApi.getState() as RootState;

//   return surveyApi
//     .fillingSurveyQuestionsHandler(store)
//     .then((res) => Promise.resolve({ data: res.data }));
// };

// const fillingOutSurveyViaAI = createAsyncThunk(
//   'survey/fillingOutSurveyViaAI',
//   fillingOutSurveyViaAIFn
// );

export const setSurveyViewVersionFn = async (
  { surveyId, versionId }: { surveyId: number; versionId: string },
  ThunkApi: TThunkApi
) => {
  return ThunkApi.dispatch(
    surveySetViewVersion({
      surveyId,
      versionId,
    })
  );
};
const setSurveyViewVersion = createAsyncThunk(
  'survey/setSurveyViewVersion',
  setSurveyViewVersionFn
);

export const setSurveyVersionFn = async (
  {
    versionId,
    providerSurveyItemData,
  }: { versionId: string; providerSurveyItemData: ISurveyItem },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const surveyToken =
    providerSurveyItemData.editToken ||
    providerSurveyItemData.changeDataToken ||
    providerSurveyItemData.reportToken ||
    providerSurveyItemData.fillToken;
  return surveyApi.setVersion(versionId, surveyToken, store).then(
    (res) => {
      ThunkApi.dispatch(
        addSurvey({
          survey: res.data,
          providerItemId: providerSurveyItemData.id,
          providerId: providerSurveyItemData.providerId,
          surveyToken: surveyToken,
        })
      );
      return Promise.resolve({ survey: res.data });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const setSurveyVersion = createAsyncThunk(
  'survey/setSurveyVersion',
  setSurveyVersionFn
);

export const deleteSurveyVersionFn = async (
  {
    versionId,
    providerSurveyItemData,
  }: { versionId: string; providerSurveyItemData: ISurveyItem },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const surveyToken =
    providerSurveyItemData.editToken ||
    providerSurveyItemData.changeDataToken ||
    providerSurveyItemData.reportToken ||
    providerSurveyItemData.fillToken;
  return surveyApi.deleteVersion(versionId, surveyToken, store).then(
    (res: any) => {
      if (!res.data?.status) {
        ThunkApi.dispatch(
          addSurvey({
            survey: res.data,
            providerItemId: providerSurveyItemData.id,
            providerId: providerSurveyItemData.providerId,
            surveyToken: surveyToken,
          })
        );
        return Promise.resolve({
          status: 'OK',
          message: res.data,
        });
      }

      return Promise.resolve({
        status: res.data.status,
        message: JSON.parse(res.data.message),
      });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const deleteSurveyVersion = createAsyncThunk(
  'survey/deleteSurveyVersion',
  deleteSurveyVersionFn
);

export const requireAllSurveysFn = async (
  { surveyTokens }: { surveyTokens: Array<string> },
  ThunkApi: TThunkApi
) => {
  return manApi.getAllSurveys(surveyTokens).then((res) => {
    ThunkApi.dispatch(
      setSeveralSurvey({
        surveys: res.data,
      })
    );
  });
};
export const searchMainReportsFn = async (
  {
    mandatoryTagIds,
    providerId,
  }: { mandatoryTagIds: Array<number>; providerId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .searchMainReport(store, mandatoryTagIds, providerId)
    .then((res) => {
      ThunkApi.dispatch(
        clearAndAddSeveralReports({
          reports: res.data.map((it) => it.report),
          clearCurrent: false,
          setLoadedForSurvey: false,
        })
      );

      return Promise.resolve({
        reports: res.data,
      });
    });
};
const requireAllSurveys = createAsyncThunk(
  'survey/requireAllSurveys',
  requireAllSurveysFn
);

const searchMainReports = createAsyncThunk(
  'survey/searchMainReports',
  searchMainReportsFn
);

export const createNewQuestionTagsFn = async (
  {
    questionCopy,
    providerId,
  }: { questionCopy: IQuestionsView; providerId: number },
  ThunkApi: TThunkApi
) => {
  const newTags = (questionCopy.tags || [])
    .filter((t: any) => !t.id || typeof t.id === 'string')
    .map((t: any) => t.text);
  if (newTags.length > 0) {
    return ThunkApi.dispatch(
      getOrCreateTag({
        providerId,
        tagList: newTags,
      })
    )
      .unwrap()
      .then(({ tags }: { tags: any }) => {
        const tagMap = tags.reduce((all: any, curr: ITagView) => {
          all[curr.text] = curr.id;
          return all;
        }, {});

        questionCopy.tags = questionCopy.tags
          ? questionCopy.tags.map((tag: any) =>
              tag.id === null || typeof tag.id === 'string'
                ? { text: tag.text, id: tagMap[tag.text] }
                : tag
            )
          : [];

        return Promise.resolve({ questionCopy });
      })
      .catch((err: any) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  }
  return Promise.resolve({ questionCopy });
};
const createNewQuestionTags = createAsyncThunk(
  'survey/createNewQuestionTags',
  createNewQuestionTagsFn
);

export const updateQuestionFn = async (
  {
    questionData,
    providerSurveyData,
  }: {
    questionData: IQuestionsView;
    providerSurveyData: ISurveyItem;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const providerId = providerSurveyData.providerId;
  const questionCopy = JSON.parse(JSON.stringify(questionData));
  const question = await ThunkApi.dispatch(
    createNewQuestionTags({
      questionCopy,
      providerId,
    })
  )
    .unwrap()
    .then((res: any) => res.questionCopy);

  return surveyApi
    .surveyUpdateQuestion(question, providerSurveyData.editToken, store)
    .then((res) => {
      ThunkApi.dispatch(
        updateQuestionAction({
          surveyId: Number(providerSurveyData.surveyId),
          oldQuestion: questionData,
          newQuestion: res.data,
        })
      );

      return Promise.resolve({
        questions: res.data,
      });
    })
    .catch((err) => {
      ThunkApi.dispatch(
        failToUpdateQuestion({
          question: questionData,
          err: err,
          newState: 'editing',
          surveyId: Number(providerSurveyData.surveyId),
        })
      );

      return ThunkApi.rejectWithValue(err) as any;
    });
};
const updateQuestion = createAsyncThunk(
  'survey/updateQuestion',
  updateQuestionFn
);

export const reindexSurveyFn = async (
  { providerSurveyData }: { providerSurveyData: ISurveyItem },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return surveyApi.reindexSurvey(providerSurveyData.editToken, store).then(
    (res) => {
      return Promise.resolve({ survey: res.data });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const reindexSurvey = createAsyncThunk('survey/reindexSurvey', reindexSurveyFn);

export const addQuestionFn = async (
  {
    questionData,
    providerSurveyData,
    keepQuestionId = false,
    enableEditing = true,
  }: {
    questionData: IQuestionsView;
    providerSurveyData: ISurveyItem;
    keepQuestionId?: boolean;
    enableEditing?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const providerId = providerSurveyData.providerId;
  const questionCopy = cloneDeep(questionData);
  if (!keepQuestionId) questionCopy.id = undefined as any;

  const question = await ThunkApi.dispatch(
    createNewQuestionTags({ questionCopy, providerId })
  )
    .unwrap()
    .then((res: any) => res.questionCopy);

  return surveyApi
    .surveyAddQuestion(question, providerSurveyData.editToken, store)
    .then((res) => {
      ThunkApi.dispatch(
        updateQuestionAction({
          surveyId: providerSurveyData.surveyId,
          oldQuestion: questionData,
          newQuestion: res.data,
          enableEditing,
        })
      );
      return Promise.resolve({ newQuestion: res.data });
    })
    .catch((err) => {
      ThunkApi.dispatch(
        failToUpdateQuestion({
          question: questionData,
          err: err,
          newState: 'creating',
          surveyId: providerSurveyData.surveyId,
        })
      );
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const addQuestion = createAsyncThunk('survey/addQuestion', addQuestionFn);

export const requireResponsesWithUsersFn = async (
  {
    surveyItemData,
    start = 0,
    count = 20,
    instance,
    thisUserId,
    order,
    extraFilters,
    preventDraft,
  }: {
    surveyItemData: ISurveyItem;
    start: number;
    count: number;
    instance?: number;
    thisUserId?: number;
    order?: string;
    extraFilters?: IQueryFilter[];
    preventDraft?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  const dateQuery = {
    filters: extraFilters || [],
    buckets: [],
    metrics: [],
  } as IAnalyticsQuery;

  if (instance)
    dateQuery.filters.push({
      field: 'instance',
      values: [instance],
      type: 'termFilter',
    });

  if (!preventDraft) {
    dateQuery.filters.push({
      field: 'draft',
      query: '*:*',
      type: 'generalTermFilter',
    });
  }

  const token =
    surveyItemData.editToken ||
    surveyItemData.changeDataToken ||
    surveyItemData.reportToken ||
    surveyItemData.fillToken;

  return surveyApi
    .analyseList(
      store,
      dateQuery,
      token,
      undefined,
      surveyItemData.extraOwnerTokens?.[0],
      start,
      count,
      order
    )
    .then(({ data: responsesAndsurvey }) => {
      return processResponses({
        responses: responsesAndsurvey.response,
        surveyItemData: surveyItemData,
        thisUserId: thisUserId,
        dispatch: ThunkApi.dispatch,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireResponsesWithUsers = createAsyncThunk(
  'survey/requireResponsesWithUsers',
  requireResponsesWithUsersFn
);

export const requireResponseWithIdFn = async (
  {
    surveyItemData,
    responseId,
    includeDeletedResponse = false,
  }: {
    surveyItemData: ISurveyItem;
    responseId: number;
    includeDeletedResponse: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const token =
    surveyItemData.editToken ||
    surveyItemData.changeDataToken ||
    surveyItemData.reportToken ||
    surveyItemData.fillToken;

  return surveyApi
    .surveyGetResponse(store, token, responseId, includeDeletedResponse)
    .then(({ data: responses }) => {
      return processResponses({
        responses: [responses],
        surveyItemData: surveyItemData,
        thisUserId: undefined,
        dispatch: ThunkApi.dispatch,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireResponseWithId = createAsyncThunk(
  'survey/requireResponseWithId',
  requireResponseWithIdFn
);

export const removeResponseFn = async (
  {
    responseId,
    surveyItemData,
  }: { responseId: number; surveyItemData: ISurveyItem },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const surveyToken =
    surveyItemData.editToken ||
    surveyItemData.changeDataToken ||
    surveyItemData.reportToken ||
    surveyItemData.fillToken;

  return surveyApi
    .removeResponse(store, responseId, surveyToken)
    .then(() => {
      ThunkApi.dispatch(
        removeSurveyResponse({
          responseId,
          surveyId: surveyItemData.surveyId,
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const removeResponse = createAsyncThunk(
  'survey/removeResponse',
  removeResponseFn
);

export const updateResponseFn = async (
  {
    response,
    surveyItemData,
    dontSendNotification,
  }: {
    response: ISurveyResponseView;
    surveyItemData: ISurveyItem;
    dontSendNotification: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const { surveyId } = surveyItemData;
  const surveyToken =
    surveyItemData.editToken ||
    surveyItemData.changeDataToken ||
    surveyItemData.reportToken ||
    surveyItemData.fillToken;
  const ownerToken = surveyItemData.extraOwnerTokens?.[0];
  const { surveyAnswerList: oldAnswer } = response;

  try {
    const updatedResponse = await surveyApi.updateResponse(
      store,
      surveyToken,
      response,
      dontSendNotification,
      ownerToken
    );
    const { surveyAnswerList: newAnswer } = updatedResponse.data;
    const fileQuestions: IFileAnswerView[] = newAnswer.filter(
      (answer: IFileAnswerView) => answer.file && answer.file.uploading === true
    );
    if (fileQuestions.length > 0) {
      try {
        await uploadSurveyFiles(fileQuestions, oldAnswer, ThunkApi);
        fileQuestions.forEach((question: IFileAnswerView) => {
          question.file.uploading = false;
          question.justUploaded = true;
        });
        updatedResponse.data.draft = response.draft;
        updatedResponse.data.extraInfo = response.extraInfo;

        await ThunkApi.dispatch(
          updateResponse({
            response: updatedResponse.data,
            surveyItemData,
            dontSendNotification,
          })
        )
          .unwrap()
          .then(() => {
            return Promise.resolve({ response: updatedResponse });
          });

        return Promise.resolve({ response: updatedResponse });
      } catch (err) {
        return ThunkApi.rejectWithValue(err) as any;
      }
    } else {
      ThunkApi.dispatch(
        addResponseAction({
          response: updatedResponse.data,
          surveyId: surveyId,
          providerId: surveyItemData.providerId,
          itemId: surveyItemData.id,
          effectiveDateString: 'filled',
        })
      );
      ThunkApi.dispatch(
        providerAddSurveyResponse({
          response: updatedResponse.data,
          surveyId: surveyId,
          providerId: surveyItemData.providerId,
          itemId: surveyItemData.id,
          effectiveDateString: 'filled',
        })
      );
      return Promise.resolve({ response: updatedResponse });
    }
  } catch (err) {
    return ThunkApi.rejectWithValue(err) as any;
  }
};
const updateResponse = createAsyncThunk(
  'survey/updateResponse',
  updateResponseFn
);
export const addResponseFn = async (
  {
    response,
    surveyItemData,
    dontSendNotification,
    periodType,
  }: {
    response: ISurveyResponseView;
    surveyItemData: ISurveyItem;
    dontSendNotification?: boolean;
    periodType: PeriodType;
  },
  ThunkApi: TThunkApi
): Promise<any> => {
  const store = ThunkApi.getState() as RootState;
  const providers = store.providers;
  const local = surveyItemData.surveyId
    ? providers.map?.[surveyItemData.providerId]?.data?.surveyOwnerProperties
        .localId || getCalendarLocal()
    : getCalendarLocal();
  try {
    const newResponse = await surveyApi.submitNewResponse(
      store,
      surveyItemData.changeDataToken || surveyItemData.fillToken,
      response,
      dontSendNotification || false,
      surveyItemData.extraOwnerTokens?.[0]
    );
    const { surveyAnswerList: oldAnswer } = response;
    const { surveyAnswerList: newAnswer } = newResponse.data;
    const fileQuestions = newAnswer.filter(
      (answer: IFileAnswerView) => answer.file && answer.file.uploading === true
    );
    if (fileQuestions.length > 0) {
      try {
        await uploadSurveyFiles(fileQuestions, oldAnswer, ThunkApi);

        fileQuestions.forEach((question: IFileAnswerView) => {
          question.file.uploading = false;
          question.justUploaded = true;
        });
        if (!response.draft) {
          newResponse.data.draft = false;
        }
        newResponse.data.extraInfo = response.extraInfo;
        return await addResponseFn(
          {
            response: newResponse.data,
            surveyItemData: surveyItemData,
            dontSendNotification: dontSendNotification || false,
            periodType: periodType,
          },
          ThunkApi
        );
      } catch (err) {
        return ThunkApi.rejectWithValue(err) as any;
      }
    } else {
      ThunkApi.dispatch(
        addResponseAction({
          response: newResponse.data,
          surveyId: surveyItemData.surveyId,
          providerId: surveyItemData.providerId,
          itemId: surveyItemData.id,
          effectiveDateString: getEffectiveName(
            periodType as TimeScale,
            response.effectiveDate || new Date().getTime(),
            local
          )?.toString(),
        })
      );
      ThunkApi.dispatch(
        providerAddSurveyResponse({
          response: newResponse.data,
          surveyId: surveyItemData.surveyId,
          providerId: surveyItemData.providerId,
          itemId: surveyItemData.id,
          effectiveDateString: getEffectiveName(
            periodType as TimeScale,
            response.effectiveDate || new Date().getTime(),
            local
          )?.toString(),
        })
      );
      return Promise.resolve({ response: newResponse });
    }
  } catch (err2) {
    return ThunkApi.rejectWithValue(err2);
  }
};
const addResponse = createAsyncThunk('survey/addResponse', addResponseFn);

export const importResponseFn = async (
  {
    file,
    surveyItemData,
    dontSendNotification,
    companyId,
  }: {
    file: File;
    surveyItemData: ISurveyItem;
    dontSendNotification: boolean;
    companyId: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return surveyApi
    .importResponsesFromExcel(
      store,
      file,
      surveyItemData.changeDataToken || surveyItemData.fillToken,
      surveyItemData.instanceType === 'User' ? 0 : surveyItemData.instance,
      surveyItemData.tags.map((it) => Number(it.id)),
      dontSendNotification,
      companyId
    )
    .then((result) => {
      ThunkApi.dispatch(
        addSurveyResponses({
          surveyId: surveyItemData.surveyId,
          responses: result.data.inserted,
        })
      );
      return Promise.resolve(result);
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const importResponse = createAsyncThunk(
  'survey/importResponse',
  importResponseFn
);

export const getReportsFn = async (
  { surveyToken }: { surveyToken: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return surveyApi
    .getReports(store, surveyToken)
    .then((res) => {
      ThunkApi.dispatch(
        clearAndAddSeveralReports({
          reports: res.data,
          clearCurrent: false,
          setLoadedForSurvey: true,
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};

const getReports = createAsyncThunk('survey/getReports', getReportsFn);

export const generateReportsFn = async (
  {
    questionId,
    config,
    surveyToken,
    updateExisting,
  }: {
    questionId: number | undefined;
    config: { [key: string]: string };
    surveyToken: string;
    updateExisting?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return surveyApi
    .generateReports(store, questionId, config, surveyToken, updateExisting)
    .then((res) => {
      ThunkApi.dispatch(
        clearAndAddSeveralReports({
          reports: res.data,
          clearCurrent: false,
          setLoadedForSurvey: false,
        })
      );
      return Promise.resolve({ reports: res.data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const generateReports = createAsyncThunk(
  'survey/generateReports',
  generateReportsFn
);

export const getReportByIdsFn = async (
  { reportIds }: { reportIds: Array<number> },
  ThunkApi: TThunkApi
) => {
  return surveyApi
    .getReportByIds(reportIds)
    .then((res) => {
      ThunkApi.dispatch(
        clearAndAddSeveralReports({
          reports: res.data,
          clearCurrent: false,
          setLoadedForSurvey: false,
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getReportByIds = createAsyncThunk(
  'survey/getReportByIds',
  getReportByIdsFn
);

export const saveReportFn = async (
  {
    report,
    isUpdate = false,
    surveyToken,
    reportToken,
    ownerToken,
  }: {
    report: IReportView;
    isUpdate: boolean;
    surveyToken?: string;
    reportToken?: string;
    ownerToken?: string;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  if (isUpdate) {
    return surveyApi
      .updateReport(store, report, surveyToken, reportToken, ownerToken)
      .then((res) => {
        ThunkApi.dispatch(updateSurveyReport({ report: res.data }));
        return Promise.resolve({ reportId: report.id });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  } else {
    return surveyApi
      .addReport(store, report, surveyToken, reportToken, ownerToken)
      .then((res) => {
        ThunkApi.dispatch(addSurveyReport({ report: res.data }));
        return Promise.resolve({ reportId: res.data.id });
      })
      .catch((err) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  }
};
const saveReport = createAsyncThunk('survey/saveReport', saveReportFn);

export const removeReportFn = async (
  {
    reportId,
    surveyToken,
    reportToken,
    ownerToken,
  }: {
    reportId: number;
    surveyToken?: string;
    reportToken?: string;
    ownerToken?: string;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return surveyApi
    .removeReport(store, reportId, surveyToken, reportToken, ownerToken)
    .then(() => {
      ThunkApi.dispatch(removeSurveyReport({ reportId }));
      return Promise.resolve({ reportId });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const removeReport = createAsyncThunk('survey/removeReport', removeReportFn);

export const getAnalyseFn = async (
  {
    dataQuery,
    surveyToken,
    ownerToken,
    reportToken,
  }: {
    dataQuery: IAnalyticsQuery;
    surveyToken?: string;
    ownerToken?: string;
    reportToken?: string;
    useMethodGet?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return surveyApi
    .analyse(store, dataQuery, surveyToken, ownerToken, reportToken)
    .then((res) => {
      return Promise.resolve({ result: res.data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getAnalyse = createAsyncThunk('survey/getAnalyse', getAnalyseFn);

export const exportResponsesFn = async (
  {
    dataQuery,
    surveyToken,
    ownerToken,
    reportToken,
  }: {
    dataQuery: IExportResponsesQuery;
    surveyToken?: string;
    ownerToken?: string;
    reportToken?: string;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return surveyApi
    .exportResponses(store, dataQuery, surveyToken, ownerToken, reportToken)
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const fileNameHeader = 'x-suggested-filename';
      const suggestedFileName = response.headers[fileNameHeader];
      const effectiveFileName =
        suggestedFileName === undefined
          ? 'export.xlsx'
          : decodeURIComponent(suggestedFileName);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', effectiveFileName); //or any other extension
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const exportResponses = createAsyncThunk(
  'survey/exportResponses',
  exportResponsesFn
);

export const canIFillFn = async (
  {
    response,
    surveyItemData,
    includeDraft,
  }: {
    response: ISurveyResponseView;
    surveyItemData: ISurveyItem;
    includeDraft?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const surveyToken =
    surveyItemData.editToken ||
    surveyItemData.changeDataToken ||
    surveyItemData.reportToken ||
    surveyItemData.fillToken;
  const ownerToken = surveyItemData.extraOwnerTokens?.[0];
  if (!response.extraOwnerTokens && ownerToken)
    response.extraOwnerTokens = [ownerToken];
  return surveyApi
    .canIFill(store, response, surveyToken, includeDraft, ownerToken)
    .then((res) => {
      return Promise.resolve({
        duplicateAnswerId: res.data.id as NumberOrNull,
        answer: res.data,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const canIFill = createAsyncThunk('survey/canIFill', canIFillFn);

export const getNotificationsFn = async (
  {
    response,
    surveyItemData,
  }: {
    response: ISurveyResponseView;
    surveyItemData: ISurveyItem;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const surveyToken =
    surveyItemData.editToken ||
    surveyItemData.changeDataToken ||
    surveyItemData.reportToken ||
    surveyItemData.fillToken;
  return surveyApi
    .getNotifications(store, response, surveyToken)
    .then((res) => {
      return Promise.resolve({ notifications: res.data });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getNotifications = createAsyncThunk(
  'survey/getNotifications',
  getNotificationsFn
);

export const simpleRequireSurveyFn = async (
  { surveyToken }: { surveyToken: string },
  ThunkApi: TThunkApi
) => {
  return surveyApi
    .getSurvey(surveyToken)
    .then((res) => {
      ThunkApi.dispatch(
        addSurvey({
          survey: res.data,
          providerItemId: 0,
          providerId: 0,
          surveyToken: surveyToken,
        })
      );
      return Promise.resolve({ surveyId: res.data.id });
    })
    .catch((err: any) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const simpleRequireSurvey = createAsyncThunk(
  'survey/simpleRequireSurvey',
  simpleRequireSurveyFn
);

export const requireCheckPointsFn = async (
  {
    surveyId,
    reportId,
    surveyToken,
    ownerToken,
    reportToken,
  }: {
    surveyId?: number;
    reportId?: number;
    surveyToken?: string;
    ownerToken?: string;
    reportToken?: string;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return surveyApi
    .getCheckPoints(
      store,
      surveyId,
      reportId,
      surveyToken,
      ownerToken,
      reportToken
    )
    .then((res) => {
      ThunkApi.dispatch(setSeveralCheckPoints({ checkPoints: res.data }));
      return Promise.resolve();
    })
    .catch((err: any) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireCheckPoints = createAsyncThunk(
  'survey/requireCheckPoints',
  requireCheckPointsFn
);

export const requireAllCheckpointsFn = async (
  {
    reportsIds,
    reportToken,
    ownerToken,
  }: { reportsIds: number[]; reportToken?: string; ownerToken?: string },
  ThunkApi: TThunkApi
) => {
  return manApi
    .getAllCheckpoints(reportsIds, reportToken, ownerToken)
    .then((res) => {
      const checkpoints: ICheckPoint[] = Object.values(res.data).flat();
      ThunkApi.dispatch(setSeveralCheckPoints({ checkPoints: checkpoints }));
      return Promise.resolve();
    })
    .catch((err: any) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireAllCheckpoints = createAsyncThunk(
  'survey/requireAllCheckpoints',
  requireAllCheckpointsFn
);

export const saveCheckPointFn = async (
  {
    checkPoint,
    isUpdate = false,
    surveyToken,
    ownerToken,
  }: {
    checkPoint: ICheckPoint;
    isUpdate: boolean;
    surveyToken?: string;
    ownerToken?: string;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  if (isUpdate) {
    return surveyApi
      .updateCheckPoint(store, checkPoint, surveyToken, ownerToken)
      .then((res) => {
        ThunkApi.dispatch(setCheckPoint({ checkPoint: res.data }));
        return Promise.resolve();
      })
      .catch((err: any) => {
        return ThunkApi.rejectWithValue(err) as any;
      });
  }
  return surveyApi
    .addCheckPoint(store, checkPoint, surveyToken, ownerToken)
    .then((res) => {
      ThunkApi.dispatch(setCheckPoint({ checkPoint: res.data }));
      return Promise.resolve();
    })
    .catch((err: any) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const saveCheckPoint = createAsyncThunk(
  'survey/saveCheckPoint',
  saveCheckPointFn
);

export const deleteCheckPointFn = async (
  {
    checkPointId,
    surveyToken,
    ownerToken,
  }: {
    checkPointId: number;
    surveyToken: string;
    ownerToken: string;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return surveyApi
    .deleteCheckPoint(store, checkPointId, surveyToken, ownerToken)
    .then(() => {
      ThunkApi.dispatch(deleteCheckPointAction({ checkPointId: checkPointId }));
      return Promise.resolve();
    })
    .catch((err: any) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const deleteCheckPoint = createAsyncThunk(
  'survey/deleteCheckPoint',
  deleteCheckPointFn
);

export const getAnalyseListFn = async (
  {
    dataQuery,
    surveyToken,
    ownerToken,
    reportToken,
    from,
    count,
    questionIdsFilter,
  }: {
    dataQuery: IAnalyticsListQuery;
    surveyToken?: string;
    ownerToken?: string;
    reportToken?: string;
    from?: number;
    count?: number;
    questionIdsFilter?: string[];
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  try {
    const { data } = await surveyApi.analyseList(
      store,
      dataQuery,
      surveyToken,
      ownerToken,
      reportToken,
      from,
      count,
      '',
      questionIdsFilter
    );
    return Promise.resolve({ result: data });
  } catch (err) {
    return ThunkApi.rejectWithValue(err) as any;
  }
};
const getAnalyseList = createAsyncThunk(
  'survey/getAnalyseList',
  getAnalyseListFn
);

export const filterResponsesByDistinctiveFn = async (
  {
    filters,
    surveyItem,
    preventDraft = false,
  }: {
    filters: IAnalyticsListQuery['filters'];
    surveyItem: ISurveyItem;
    preventDraft?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const surveyToken =
    surveyItem.editToken ||
    surveyItem.changeDataToken ||
    surveyItem.reportToken ||
    surveyItem.fillToken;

  if (!preventDraft) {
    filters.push({
      field: 'draft',
      query: '*:*',
      type: 'generalTermFilter',
    });
  }
  try {
    const {
      data: { response, relatedSurveys },
    } = await surveyApi.analyseList(
      store,
      { filters },
      surveyToken,
      undefined,
      surveyItem.extraOwnerTokens?.[0]
    );

    if (response.length > 0) {
      const usersSet = getUsersIdsFromResponses({
        responses: response,
        includeInstanceAsUserId: surveyItem.instanceType === 'User',
      });

      return ThunkApi.dispatch(requireSeveralUser({ usersList: usersSet }))
        .unwrap()
        .then(() => {
          // const currentResponsesIds: Array<number> = store.survey.data[
          //   surveyItem.surveyId
          // ]?.data?.responseMap
          //   ? Object.keys(
          //       store.survey.data[surveyItem.surveyId].data.responseMap!
          //     ).map((it) => Number(it))
          //   : [];

          const filteredResponsesIds = response.map((r) => r.id as number);
          // const hasAllResponses = filteredResponsesIds.every((responseId) =>
          //   currentResponsesIds.includes(responseId)
          // );

          // if (!hasAllResponses) {
          // const filteredResponses = response.filter(
          //   (r) => !currentResponsesIds.includes(r.id as number)
          // );
          ThunkApi.dispatch(
            addSurveyResponses({
              surveyId: relatedSurveys[0].id,
              responses: response,
            })
          );
          // }
          return Promise.resolve({ filteredResponsesIds });
        });
    } else {
      return Promise.resolve({ filteredResponsesIds: [] });
    }
  } catch (err) {
    return ThunkApi.rejectWithValue(err) as any;
  }
};
const filterResponsesByDistinctive = createAsyncThunk(
  'survey/filterResponsesByDistinctive',
  filterResponsesByDistinctiveFn
);

export const cumulativePercentageReadyFn = async (
  {
    groupId,
    key,
    percentage,
    boundPercentage,
  }: {
    groupId: string;
    key: string;
    percentage: number;
    boundPercentage: number;
  },
  ThunkApi: TThunkApi
) => {
  ThunkApi.dispatch(
    cumulativePercentageReadyAction({
      groupId: groupId,
      key: key,
      percentage: percentage,
      boundPercentage: boundPercentage,
    })
  );
  return Promise.resolve();
};
const cumulativePercentageReady = createAsyncThunk(
  'survey/cumulativePercentageReady',
  cumulativePercentageReadyFn
);

export const cumulativePercentageResetFn = async (
  { groupId }: { groupId: string },
  ThunkApi: TThunkApi
) => {
  ThunkApi.dispatch(cumulativePercentageResetAction({ groupId: groupId }));
  return Promise.resolve();
};
const cumulativePercentageReset = createAsyncThunk(
  'survey/cumulativePercentageReset',
  cumulativePercentageResetFn
);

export const addToPhoneValidListFn = async (
  {
    phoneValidList,
    providerSurveyItemData,
  }: {
    phoneValidList: string[];
    providerSurveyItemData: ISurveyItem;
  },
  ThunkAPI: TThunkApi
) => {
  const state = ThunkAPI.getState() as RootState;
  const surveyToken = providerSurveyItemData.editToken;
  return surveyApi
    .addToPhoneValidList(phoneValidList, surveyToken, state)
    .then((res) => {
      ThunkAPI.dispatch(
        addSurvey({
          survey: res.data,
          providerItemId: providerSurveyItemData.id,
          providerId: providerSurveyItemData.providerId,
          surveyToken,
        })
      );
    });
};
export const applyAnalyseTemplateFn = async (
  {
    providerId,
  }: {
    providerId: number;
  },
  ThunkAPI: TThunkApi
) => {
  const state = ThunkAPI.getState() as RootState;
  return manApi.updateAnalyseSurveys(providerId, state);
};

const addToPhoneValidList = createAsyncThunk(
  'survey/addToPhoneValidList',
  addToPhoneValidListFn
);

const applyAnalyseTemplate = createAsyncThunk(
  'survey/applyAnalyseTemplate',
  applyAnalyseTemplateFn
);

export const removePhoneValidListFn = async (
  { providerSurveyItemData }: { providerSurveyItemData: ISurveyItem },
  ThunkAPI: TThunkApi
) => {
  const state = ThunkAPI.getState() as RootState;
  const surveyToken = providerSurveyItemData.editToken;
  return surveyApi.removePhoneValidList(surveyToken, state).then((res) => {
    ThunkAPI.dispatch(
      addSurvey({
        survey: res.data,
        providerItemId: providerSurveyItemData.id,
        providerId: providerSurveyItemData.providerId,
        surveyToken,
      })
    );
  });
};

const removePhoneValidList = createAsyncThunk(
  'survey/addToPhoneValidList',
  removePhoneValidListFn
);

const surveySlice = createSlice({
  name: 'survey',
  initialState: initialState,
  reducers: {
    loadingSurvey: loadingSurveyHandler,
    addSurvey: addSurveyHandler,
    surveySetViewVersion: surveySetViewVersionHandler,
    failToAddSurvey: failToAddSurveyHandler,
    addQuestionAction: addQuestionHandler,
    updateQuestionAction: updateQuestionHandler,
    failToUpdateQuestion: failToUpdateQuestionHandler,
    changeQuestionState: changeQuestionStateHandler,
    addSurveyResponses: addSurveyResponsesHandler,
    allResponsesLoaded: allResponsesLoadedHandler,
    removeSurveyResponse: removeSurveyResponseHandler,
    addSurveyResponse: addSurveyResponseHandler,
    clearAndAddSeveralReports: clearAndAddSeveralReportsHandler,
    addSurveyReport: addSurveyReportHandler,
    updateSurveyReportColorMapAction: updateSurveyReportColorMapHandler,
    updateSurveyReport: updateSurveyReportHandler,
    removeSurveyReport: removeSurveyReportHandler,
    setSeveralCheckPoints: setSeveralCheckPointsHandler,
    setCheckPoint: setCheckPointHandler,
    deleteCheckPointAction: deleteCheckPointHandler,
    setSeveralSurvey: setSeveralSurveyHandler,
    cumulativePercentageReadyAction: cumulativePercentageReadyHandler,
    cumulativePercentageResetAction: cumulativePercentageResetHandler,
    addResponseAction: addResponseHandler,
    initUploadProgressAction: initUploadProgressHandler,
    setQuestionProgressAction: setQuestionProgressHandler,
    clearUploadProgressAction: clearUploadProgressHandler,
  },
});

export const {
  loadingSurvey,
  addSurvey,
  surveySetViewVersion,
  failToAddSurvey,
  addQuestionAction,
  updateQuestionAction,
  failToUpdateQuestion,
  changeQuestionState,
  addSurveyResponses,
  allResponsesLoaded,
  removeSurveyResponse,
  addSurveyResponse,
  clearAndAddSeveralReports,
  addSurveyReport,
  updateSurveyReport,
  removeSurveyReport,
  setSeveralCheckPoints,
  setCheckPoint,
  deleteCheckPointAction,
  setSeveralSurvey,
  cumulativePercentageReadyAction,
  cumulativePercentageResetAction,
  addResponseAction,
  initUploadProgressAction,
  setQuestionProgressAction,
  clearUploadProgressAction,
  updateSurveyReportColorMapAction,
} = surveySlice.actions;

export {
  loadSurvey,
  requireSurvey,
  updateSurvey,
  createSurveyVersion,
  setSurveyViewVersion,
  setSurveyVersion,
  requireAllSurveys,
  searchMainReports,
  createNewQuestionTags,
  updateQuestion,
  reindexSurvey,
  addQuestion,
  requireResponsesWithUsers,
  requireResponseWithId,
  removeResponse,
  updateResponse,
  addResponse,
  importResponse,
  getReports,
  generateReports,
  getReportByIds,
  saveReport,
  removeReport,
  getAnalyse,
  exportResponses,
  canIFill,
  getNotifications,
  simpleRequireSurvey,
  requireCheckPoints,
  requireAllCheckpoints,
  saveCheckPoint,
  deleteCheckPoint,
  getAnalyseList,
  filterResponsesByDistinctive,
  cumulativePercentageReady,
  cumulativePercentageReset,
  addToPhoneValidList,
  removePhoneValidList,
  applyAnalyseTemplate,
  deleteSurveyVersion,
};

export default surveySlice.reducer;
